import React, { useState, useCallback } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import CoursesPage, {
  Container,
} from '@fuww/library/src/Layouts/CoursesPage';
import Anchor from '@fuww/library/src/Anchor';
import Layout from '../components/Layout';
import Hero from '../components/Hero';
import Head from '../components/Head';
import CourseList from '../components/Education/CourseList';
import coursesQuery from '../lib/queries/courses/coursesQuery.graphql';
import courseCategoriesQuery
  from '../lib/queries/courses/courseCategoriesQuery.graphql';
import CategorySelect from '../components/Education/CourseCategories';
import withQuery from '../lib/withQuery';
import Error from '../components/Error';
import HomeLink from '../components/HomeLink';
import LinkAndAnchor from '../components/LinkAndAnchor';
import debouncedReplaceRoute from '../lib/debouncedReplaceRoute';
import withApollo from '../lib/apollo';
import replaceRoute from '../lib/replaceRoute';
import { coursesListLogoImageConfigurations } from '../lib/imageConfigurations';

export const ALL_CATEGORIES = 1;
const LIMIT = 20;

const headerImageUrls = {
  mobile: [
    'https://r.fashionunited.com/DAhSvNHxiYJ5-8mCdS13WEZPHlpS-T5rDtHJ0TXQqlw/resize:fill:42:34:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbl9jb3Vyc2VzX2RhdGFiYXNlX2Jhbm5lcl9tb2JpbGUuanBn',
    'https://r.fashionunited.com/UpFTU04cBkEJPb9kiFDeByTPtUm6Gw3nE2SPfUjDqA8/resize:fill:360:288:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbl9jb3Vyc2VzX2RhdGFiYXNlX2Jhbm5lcl9tb2JpbGUuanBn',
    'https://r.fashionunited.com/0NT5qcFJRSa6hK8jd2K9gofkLOvXssK4o15etypwJbw/resize:fill:720:576:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbl9jb3Vyc2VzX2RhdGFiYXNlX2Jhbm5lcl9tb2JpbGUuanBn',
  ],
  desktop: [
    'https://r.fashionunited.com/sVlkIfZ45dO49i1xGqbjZNRwM6hkssp02hR9HzLWunM/resize:fill:42:8:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbl9jb3Vyc2VzX2RhdGFiYXNlX2Jhbm5lcl9kZXNrdG9wLmpwZw',
    'https://r.fashionunited.com/LPjscSGM6IC2Gy8VTs1VPIV2sXDdtwkPduzgsX5geCE/resize:fill:1350:277:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbl9jb3Vyc2VzX2RhdGFiYXNlX2Jhbm5lcl9kZXNrdG9wLmpwZw',
    'https://r.fashionunited.com/9IHWhRfIN6fv7ZL9ApK2M2MHVOa82344a8BDzD_fjeA/resize:fill:2560:526:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbl9jb3Vyc2VzX2RhdGFiYXNlX2Jhbm5lcl9kZXNrdG9wLmpwZw',
    'https://r.fashionunited.com/ZYdD2ay8gFKYieUpEMAA4J-qyOmkuITysCH7UilkJBw/resize:fill:5120:1052:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbl9jb3Vyc2VzX2RhdGFiYXNlX2Jhbm5lcl9kZXNrdG9wLmpwZw',
  ],
};

const Courses = ({
  data, variables, fetchMore, loading,
}) => {
  const { coursesConnection: { edges } } = data;
  return (
    edges.length > 0
      ? (
        <CourseList
          data={data}
          connectionField="coursesConnection"
          variables={variables}
          fetchMore={fetchMore}
          loading={loading}
        />
      )
      : (
        <Container>
          <Error error="No courses listed" />
        </Container>
      )
  );
};

Courses.propTypes = {
  data: PropTypes.shape({
    coursesConnection: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }),
  fetchMore: PropTypes.func,
  variables: PropTypes.shape({}),
  loading: PropTypes.bool.isRequired,
};

Courses.defaultProps = {
  data: {},
  variables: null,
  fetchMore: undefined,
};

const CourseListWithQuery = withQuery(Courses);

const CoursesListPage = () => {
  const {
    query: {
      categoryId: categoryIdParameter,
      keywords: keywordsParameter,
    },
  } = useRouter();
  const categoryId = categoryIdParameter ? Number(categoryIdParameter) : null;
  const breadcrumbs = [
    <HomeLink />,
    <Anchor
      styled
      href="https://fashionunited.com/education"
    >
      Education
    </Anchor>,
    <LinkAndAnchor
      passHref
      route="courses"
    >
      Courses
    </LinkAndAnchor>,
  ];
  const [keywords, setKeywords] = useState(keywordsParameter || '');

  const handleOnChange = useCallback((event) => {
    const value = Number(event.target.value);

    const currentCategoryId = value === ALL_CATEGORIES ? null : value;
    replaceRoute('courses', {
      categoryId: currentCategoryId,
      keywords: keywordsParameter,
    });
  }, [keywordsParameter]);

  return (
    <CoursesPage
      title="FashionUnited Courses"
      breadcrumbs={breadcrumbs}
      keywords={keywords}
      onChange={({ target: { value } }) => {
        setKeywords(value);
        debouncedReplaceRoute(
          'courses',
          value ? { keywords: value, categoryId } : { categoryId },
        );
      }}
      onClear={() => {
        setKeywords('');
        replaceRoute('courses', { categoryId });
      }}
      categorySelect={(
        <CategorySelect
          query={courseCategoriesQuery}
          value={categoryId || ALL_CATEGORIES}
          handleOnChange={handleOnChange}
          errorMessage="Error loading category list"
        />
      )}
    >
      <Head
        title="FashionUnited Courses"
        description="FashionUnited Courses"
      />
      <CourseListWithQuery
        query={coursesQuery}
        variables={{
          categoryId,
          first: LIMIT,
          keywords: keywordsParameter,
          logoConfigurations: coursesListLogoImageConfigurations,
        }}
        errorMessage="Error loading courses"
      />
    </CoursesPage>
  );
};

CoursesListPage.getLayout = (page) => (
  <Layout header={(<Hero imageUrls={headerImageUrls} />)}>
    {page}
  </Layout>
);

export default withApollo(CoursesListPage);
